import React from 'react';
import styled from 'styled-components';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { ItemTypes } from '../../utils/dragTypes';
import { toRgba } from '../../../../ui/theme/colors';
import theme from '../../../../ui/theme';
import { Icon, Typo } from '../../../../ui';

const dropZoneColor = theme.colors.PRIMARY;

// Vertical drop zone wrapper
const VerticalDropZoneWrapper = styled.div`
  width: 100%;
  min-height: ${({ $isVisible }) => ($isVisible ? '24px' : '0px')};
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 4px 0;
`;

// Vertical drop indicator
const VerticalDropIndicator = styled.div`
  width: 100%;
  height: ${({ $isOver }) => ($isOver ? '24px' : '16px')};
  border: ${({ $isOver }) =>
    $isOver ? `2px dashed ${dropZoneColor}` : `1px dashed ${toRgba(dropZoneColor, 0.3)}`};
  border-radius: 4px;
  background: ${({ $isOver }) =>
    $isOver
      ? `repeating-linear-gradient(
          45deg,
          ${toRgba(dropZoneColor, 0.08)},
          ${toRgba(dropZoneColor, 0.08)} 4px,
          ${toRgba(dropZoneColor, 0.15)} 4px,
          ${toRgba(dropZoneColor, 0.15)} 8px
        )`
      : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
`;

// Horizontal drop zone wrapper
const HorizontalDropZoneWrapper = styled.div`
  width: ${({ $isOver }) => ($isOver ? '60px' : '32px')};
  min-height: 40px;
  display: ${({ $isVisible }) => ($isVisible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
  flex-shrink: 0;
`;

// Horizontal drop indicator
const HorizontalDropIndicator = styled.div`
  width: 100%;
  height: 100%;
  min-height: 40px;
  border: ${({ $isOver }) =>
    $isOver ? `2px dashed ${dropZoneColor}` : `1px dashed ${toRgba(dropZoneColor, 0.3)}`};
  border-radius: 4px;
  background: ${({ $isOver }) =>
    $isOver
      ? `repeating-linear-gradient(
          45deg,
          ${toRgba(dropZoneColor, 0.08)},
          ${toRgba(dropZoneColor, 0.08)} 4px,
          ${toRgba(dropZoneColor, 0.15)} 4px,
          ${toRgba(dropZoneColor, 0.15)} 8px
        )`
      : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  svg {
    opacity: ${({ $isOver }) => ($isOver ? 1 : 0.5)};
    transform: scale(${({ $isOver }) => ($isOver ? 1 : 0.8)});
    transition: all 0.15s ease;
  }
`;

/**
 * OrganizerVerticalDropZone - Drop zone between rows
 * Uses react-dnd for consistency with the rest of the app
 */
export const OrganizerVerticalDropZone = ({ position, onDrop, isVisible }) => {
  const { t } = useTranslation();
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.MODULE,
      drop: (item) => {
        if (onDrop) {
          onDrop({ ...item, targetPosition: position, type: 'vertical' });
        }
        return { position, type: 'vertical' };
      },
      collect: (monitor) => ({
        isOver: monitor.isOver({ shallow: true }),
        canDrop: monitor.canDrop(),
      }),
    }),
    [position, onDrop]
  );

  return (
    <VerticalDropZoneWrapper ref={drop} $isVisible={isVisible}>
      <VerticalDropIndicator $isOver={isOver && canDrop}>
        {isOver && canDrop && (
          <Typo.Info fontWeight='600' color={dropZoneColor} $caps>
            {t('editCourse.dropHere')}
          </Typo.Info>
        )}
      </VerticalDropIndicator>
    </VerticalDropZoneWrapper>
  );
};

/**
 * OrganizerHorizontalDropZone - Drop zone within a row
 * Uses react-dnd for consistency with the rest of the app
 */
export const OrganizerHorizontalDropZone = ({ position, rowId, onDrop, isVisible }) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: ItemTypes.MODULE,
      drop: (item) => {
        if (onDrop) {
          onDrop({ ...item, targetRowId: rowId, targetPosition: position, type: 'horizontal' });
        }
        return { rowId, position, type: 'horizontal' };
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [rowId, position, onDrop]
  );

  return (
    <HorizontalDropZoneWrapper ref={drop} $isVisible={isVisible} $isOver={isOver && canDrop}>
      <HorizontalDropIndicator $isOver={isOver && canDrop}>
        <Icon.Add size='x-small' color={dropZoneColor} />
      </HorizontalDropIndicator>
    </HorizontalDropZoneWrapper>
  );
};
